import type {
  IntrinsicSystemProps,
  LabeledVariants,
  MotionPropsFor,
  SimpleGridProps,
} from '@hover/blueprint';
import {
  SimpleGrid,
  createContext,
  forwardRef,
  mapResponsive,
  motion,
  splitLayoutProps,
  system,
  useMergeRefs,
  useSafeLayoutEffect,
  useSizes,
} from '@hover/blueprint';
import type {
  LayoutProps,
  ResponsiveValue,
  SpaceProps,
} from '@hover/blueprint/chakra';
import { includes } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import type { RefObject } from 'react';
import React, {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { preloadImage } from 'src/modules/homeownerApp/helpers';

import { Skeleton } from './Skeleton';

const HEIGHT_INCREMENT = 5;

const variants = {
  item: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  },
} satisfies Record<string, LabeledVariants<'hidden' | 'visible'>>;

interface MasonryContext {
  itemPaddingBottom: SimpleGridProps['columnGap'];
  itemGridRowEnd: SimpleGridProps['gridRowEnd'];
  gridRef: RefObject<HTMLDivElement>;
}

const [Provider, useContext] = createContext<MasonryContext>({
  name: 'MasonryContext',
  strict: true,
});

const computeStyles = (grid: HTMLDivElement) => {
  const computedGap = parseInt(
    window.getComputedStyle(grid).getPropertyValue('grid-row-gap'),
    10,
  );
  const computedRowHeight = parseInt(
    window.getComputedStyle(grid).getPropertyValue('grid-auto-rows'),
    10,
  );

  return { computedGap, computedRowHeight };
};

type ComputedStyles = ReturnType<typeof computeStyles>;

const resizeItem = (
  item: Element,
  { computedGap, computedRowHeight }: ComputedStyles,
) => {
  const content = item.children[0];

  if (!content || !(item instanceof HTMLElement)) return;

  const rowSpan = Math.floor(
    (content.getBoundingClientRect().height + computedGap) /
      (computedRowHeight + computedGap),
  );

  // eslint-disable-next-line no-param-reassign
  item.style.gridRowEnd = `span ${rowSpan}`;
};

type MotionDivProps = MotionPropsFor<IntrinsicSystemProps<'div'>>;
export interface MasonryItemProps extends MotionDivProps {}

const MasonryItem = forwardRef<MasonryItemProps, 'div'>(
  ({ children, ...props }, ref) => {
    const child = Children.only(children);

    const { gridRef, itemPaddingBottom, itemGridRowEnd } = useContext();

    const resize = useCallback(
      (item: HTMLElement) => {
        const { current: grid } = gridRef;

        if (!grid) return;

        resizeItem(item, computeStyles(grid));
      },
      [gridRef],
    );

    return (
      <motion.div
        gridRowEnd={itemGridRowEnd}
        height="100%"
        onError={event => {
          const item = event.currentTarget;
          setTimeout(() => resize(item), 0);
        }}
        onLoad={event => resize(event.currentTarget)}
        sx={{
          '& > *:not(style)': {
            paddingBottom: itemPaddingBottom,
          },
        }}
        variants={variants.item}
        {...props}
        ref={ref}
      >
        {child}
      </motion.div>
    );
  },
);

type PreloadState = 'idle' | 'loading' | 'loaded';

export interface MasonryProps
  extends MotionPropsFor<
      Pick<SimpleGridProps, 'columnGap' | 'gap' | 'rowGap' | 'columns'>
    >,
    LayoutProps,
    SpaceProps {
  /**
   * Fallback row height in case automatic measurement
   * fails for some reason. This shouldn't happen.
   */
  fallbackRowHeight: ResponsiveValue<number>;
  /**
   * Controls whether the loading state is shown
   */
  isLoading?: boolean;
  /**
   * Aspect ratio of the placeholder skeleton items when in the loading state
   *
   * @defaultValue '4/3'
   */
  placeholderAspectRatio?: string;
  /**
   * Number of skeleton placeholder items to display when in the loading state
   *
   * @defaultValue 3
   */
  placeholderCount?: number;
  /**
   * Optionally pass an array of image URLs
   * to preload before displaying the grid
   */
  preloadImages?: string[];
  /**
   * Whether to display the skeleton loading state while preloading images
   * specified in `preloadImages`.
   */
  waitForImages?: boolean;
}

const MotionSimpleGrid = motion(SimpleGrid);

/**
 * A "masonry" grid component that strives to progressively enhance the native
 * CSS grid layout. A pure CSS grid has a constant row height for each row. This
 * uses JavaScript to make items span rows in order to pack them vertically in
 * the "masonry" style made popular by Pinterest.
 *
 * @see {@link https://w3bits.com/css-grid-masonry/}
 */
const Masonry = forwardRef<MasonryProps, 'div'>(
  (
    {
      children,
      columnGap,
      columns,
      fallbackRowHeight,
      gap,
      isLoading: isLoadingProp,
      placeholderAspectRatio = '4/3',
      placeholderCount = 3,
      preloadImages,
      rowGap,
      waitForImages = true,
      ...props
    },
    ref,
  ) => {
    const hasPreloadImages = preloadImages && preloadImages.length > 0;

    const [mounted, setMounted] = useState(false);
    const [preloadState, setPreloadState] = useState<PreloadState>(
      hasPreloadImages ? 'idle' : 'loaded',
    );

    const gridRef = useRef<HTMLDivElement>(null);

    const itemPaddingBottom = rowGap ?? gap;
    const itemGridRowEnd = mapResponsive(
      h => `span ${Math.round(h / HEIGHT_INCREMENT / 2)}`,
      fallbackRowHeight,
    );

    const resizeMasonryItems = useCallback(() => {
      const { current: grid } = gridRef;

      if (!grid) return;

      const styles = computeStyles(grid);

      Array.from(grid.children).forEach(item => resizeItem(item, styles));
    }, []);

    const [size] = useSizes({
      getNodes: () => [gridRef.current],
      observeMutation: true,
    });

    const columnsDependency = JSON.stringify(columns);

    useSafeLayoutEffect(() => {
      setMounted(true);
      if (preloadState === 'loaded') resizeMasonryItems();
    }, [
      size?.width,
      size?.height,
      preloadState,
      resizeMasonryItems,
      // Recalculate the items size when the number of columns changes. Note
      // that `columns` is a responsive style prop, so it's usually passed as an
      // object, so we need to serialize to a `string` to use it as an effect
      // dependency.
      columnsDependency,
    ]);

    useEffect(() => {
      if (includes(['loading', 'loaded'], preloadState) || !hasPreloadImages)
        return;

      setPreloadState('loading');

      void (async () => {
        try {
          await Promise.allSettled(
            preloadImages.map(url => preloadImage(url, { catch: false })),
          );
        } catch (_) {
          // Catch image load errors
        } finally {
          setPreloadState('loaded');
          resizeMasonryItems();
        }
      })();
    }, [hasPreloadImages, preloadImages, preloadState, resizeMasonryItems]);

    const isLoading = waitForImages
      ? isLoadingProp || preloadState !== 'loaded'
      : isLoadingProp;

    const { layoutProps } = splitLayoutProps(props);
    const gridProps = {
      columnGap,
      gap,
      rowGap,
      columns,
    } satisfies SimpleGridProps;

    return (
      <system.div boxSizing="border-box" {...layoutProps}>
        <system.div position="relative">
          <AnimatePresence mode="popLayout">
            {isLoading && (
              <MotionSimpleGrid
                boxSizing="border-box"
                exit={{ opacity: 0 }}
                width="100%"
                {...gridProps}
              >
                {Array.from({ length: placeholderCount }).map((_, index) => (
                  <Skeleton
                    isLoaded={false}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    rounded="200"
                    sx={{ aspectRatio: placeholderAspectRatio }}
                    width="100%"
                  />
                ))}
              </MotionSimpleGrid>
            )}
          </AnimatePresence>
          <MotionSimpleGrid
            animate={isLoading ? 'hidden' : 'visible'}
            autoRows={mounted ? `${HEIGHT_INCREMENT}px` : undefined}
            boxSizing="border-box"
            columnGap={columnGap ?? gap}
            columns={columns}
            initial={isLoading ? 'hidden' : false}
            position="relative"
            ref={useMergeRefs(ref, gridRef)}
            rowGap="100"
            variants={variants.container}
          >
            <Provider value={{ gridRef, itemGridRowEnd, itemPaddingBottom }}>
              {children}
            </Provider>
          </MotionSimpleGrid>
        </system.div>
      </system.div>
    );
  },
);

export { Masonry, MasonryItem };
